import React, { useState, useEffect, useRef } from 'react';
import { Book, BookImage } from '../types';
import { getImageUrl, getThumbnailUrl, getCardImageUrl } from '../services/api';
import './BookGrid.css';

export interface BookGridProps {
  books: Book[];
  onBookClick: (book: Book) => void;
  isLoading?: boolean;
  emptyMessage?: string;
}

export function BookGrid({
  books,
  onBookClick,
  isLoading = false,
  emptyMessage = 'Nenhum livro encontrado.',
}: BookGridProps): React.ReactElement {
  if (isLoading) {
    return (
      <div className="book-grid book-grid--loading">
        <div className="book-grid__loading-container">
          <div className="book-grid__spinner" />
          <p className="book-grid__loading-text">Carregando livros...</p>
        </div>
      </div>
    );
  }

  if (books.length === 0) {
    return (
      <div className="book-grid book-grid--empty">
        <div className="book-grid__empty-container">
          <span className="book-grid__empty-icon">📚</span>
          <p className="book-grid__empty-text">{emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="book-grid">
      <div className="book-grid__container">
        {books.map((book) => (
          <BookGridItem
            key={book.id}
            book={book}
            onBookClick={onBookClick}
          />
        ))}
      </div>
    </div>
  );
}

// Fontes do card em ordem de preferência: card webp -> thumb legado -> original
const CARD_IMAGE_SOURCES = [getCardImageUrl, getThumbnailUrl, getImageUrl] as const;

// Lista vazia estável (livros sem imagem não recriam array a cada render)
const EMPTY_IMAGES: BookImage[] = [];

// Primeira variante da cadeia que ainda não falhou neste card (todas falharam: repete a última, sem loopar)
function pickCardSource(filepath: string, failedUrls: Set<string>): string {
  for (const build of CARD_IMAGE_SOURCES) {
    const url = build(filepath);
    if (!failedUrls.has(url)) return url;
  }
  return getImageUrl(filepath);
}

// Pré-carrega a variante de card; se falhar, pré-carrega a thumb legado (pulando URLs que já falharam)
function preloadCardImage(filepath: string, failedUrls: Set<string>): void {
  const cardUrl = getCardImageUrl(filepath);
  if (failedUrls.has(cardUrl)) return;
  const preloader = new Image();
  preloader.onerror = () => {
    // Registra a falha para o <img> pular direto para a próxima variante
    failedUrls.add(cardUrl);
    const legacyUrl = getThumbnailUrl(filepath);
    if (!failedUrls.has(legacyUrl)) {
      const legacy = new Image();
      legacy.src = legacyUrl;
    }
  };
  preloader.src = cardUrl;
}

interface BookGridItemProps {
  book: Book;
  onBookClick: (book: Book) => void;
}

// Card memoizado: só re-renderiza quando o livro (referência estável) ou o callback mudam
const BookGridItem = React.memo(function BookGridItem({
  book,
  onBookClick,
}: BookGridItemProps): React.ReactElement {
  const images = book.images && book.images.length > 0 ? book.images : EMPTY_IMAGES;
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Cache persistente de URLs que falharam (404): nunca repetidas na vida útil do card
  const failedUrlsRef = useRef<Set<string>>(new Set());
  // Tick que força re-render quando uma URL é registrada como falha
  const [, setFailedTick] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentImageIndex(prev => (prev + 1) % images.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [images.length]);

  // Pré-carrega a próxima imagem do crossfade para a troca ser fluida
  useEffect(() => {
    if (images.length <= 1) return;
    const nextImage = images[(currentImageIndex + 1) % images.length];
    preloadCardImage(nextImage.filepath, failedUrlsRef.current);
  }, [currentImageIndex, images]);

  const currentImage =
    images.length > 0 ? images[Math.min(currentImageIndex, images.length - 1)] : null;
  const hasImages = currentImage !== null;

  const maxVisibleTags = 3;
  const visibleTags = book.tags?.slice(0, maxVisibleTags) || [];
  const remainingTagsCount = (book.tags?.length || 0) - maxVisibleTags;

  const handleClick = () => onBookClick(book);

  return (
    <article
      className="book-grid__item"
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      aria-label={`Ver detalhes de ${book.title}`}
    >
      <div className="book-grid__image-container">
        {currentImage && (
          <img
            key={currentImage.id}
            src={pickCardSource(currentImage.filepath, failedUrlsRef.current)}
            alt={`Imagem de ${book.title}`}
            className="book-grid__image"
            width={400}
            height={533}
            loading="lazy"
            decoding="async"
            onError={() => {
              // Registra a URL corrente como falha e avança na cadeia (card -> thumb -> original)
              const failedUrl = pickCardSource(currentImage.filepath, failedUrlsRef.current);
              if (!failedUrlsRef.current.has(failedUrl)) {
                failedUrlsRef.current.add(failedUrl);
                setFailedTick((tick) => tick + 1);
              }
            }}
          />
        )}
        <div
          className="book-grid__image-placeholder"
          style={{ display: hasImages ? 'none' : 'flex' }}
        >
          <span className="book-grid__placeholder-icon">📚</span>
          <span className="book-grid__placeholder-text">Sem imagem</span>
        </div>

        {book.physicallyAvailable && (
          <div
            className="book-grid__availability-badge"
            title="Livro disponível fisicamente"
            aria-label="Livro disponível fisicamente"
          >
            <span className="book-grid__availability-icon">✓</span>
            <span className="book-grid__availability-text">Disponível</span>
          </div>
        )}

        {book.isAwarded && (
          <div
            className="book-grid__awarded-badge"
            title="Livro Premiado"
            aria-label="Livro premiado"
          >
            <span className="book-grid__awarded-icon">🏆</span>
          </div>
        )}

        {images.length > 1 && (
          <div className="book-grid__image-dots">
            {images.map((_, index) => (
              <span
                key={index}
                className={`book-grid__image-dot ${index === currentImageIndex ? 'book-grid__image-dot--active' : ''}`}
              />
            ))}
          </div>
        )}
      </div>

      <div className="book-grid__info">
        <h3 className="book-grid__title" title={book.title}>
          {book.title}
        </h3>

        {book.publicationYear && (
          <span className="book-grid__year">{book.publicationYear}</span>
        )}

        {visibleTags.length > 0 && (
          <div className="book-grid__tags">
            {visibleTags.map((tag) => (
              <span key={tag.id} className="book-grid__tag" title={tag.name}>
                {tag.name}
              </span>
            ))}
            {remainingTagsCount > 0 && (
              <span className="book-grid__tag book-grid__tag--more">
                +{remainingTagsCount}
              </span>
            )}
          </div>
        )}
      </div>
    </article>
  );
});

export default BookGrid;
